import React, { useEffect, useState, useMemo } from 'react';
import api from '../services/api';

const BRAND   = '#4A7EB5';
const BG      = '#0F1923';
const SURFACE = '#1A2B3C';
const BORDER  = '#2D4259';
const MUTED   = '#8FA8C0';
const SUCCESS = '#10B981';

const STATUS_META: Record<string, { label: string; color: string }> = {
  nh1:  { label: 'NH1',  color: MUTED },
  nh2:  { label: 'NH2',  color: MUTED },
  nh3:  { label: 'NH3',  color: '#4A6580' },
  gb:   { label: 'Go Back', color: '#F59E0B' },
  cb:   { label: 'Call Back', color: '#8B5CF6' },
  app:  { label: 'Appointment', color: BRAND },
  ld:   { label: 'Lead', color: '#06B6D4' },
  sold: { label: 'SOLD', color: SUCCESS },
};

export default function TeamActivityPage() {
  const [leads, setLeads]         = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [repFilter, setRepFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch]       = useState('');
  const [dateFilter, setDateFilter] = useState('all');

  const ONLINE_SOURCES = ['meta', 'google', 'website', 'facebook', 'instagram'];

  const [salesmanIds, setSalesmanIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    Promise.all([
      api.get('/leads'),
      api.get('/leaderboard'), // leaderboard only includes salesman-role users
    ]).then(([leadsRes, lbRes]) => {
      // Build set of salesman IDs from leaderboard (salesmen only)
      const ids = new Set<string>((lbRes.data || []).map((s: any) => s.id));
      setSalesmanIds(ids);
      // Only field leads (with salesman_id) from actual salesmen, no online/inbound leads
      const fieldLeads = (leadsRes.data || []).filter((l: any) =>
        l.salesman_id &&
        ids.has(l.salesman_id) &&
        !ONLINE_SOURCES.includes(l.lead_source)
      );
      setLeads(fieldLeads);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  // Get unique reps
  const reps = useMemo(() => {
    const map = new Map<string, string>();
    leads.forEach(l => { if (l.salesman_id && l.salesman?.name) map.set(l.salesman_id, l.salesman.name); });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [leads]);

  // Date filter helper
  const isInDateRange = (dateStr: string) => {
    if (dateFilter === 'all') return true;
    const d = new Date(dateStr);
    const now = new Date();
    if (dateFilter === 'today') return d.toDateString() === now.toDateString();
    if (dateFilter === 'week') {
      const weekAgo = new Date(now); weekAgo.setDate(weekAgo.getDate() - 7);
      return d >= weekAgo;
    }
    if (dateFilter === 'month') {
      const monthAgo = new Date(now); monthAgo.setDate(monthAgo.getDate() - 30);
      return d >= monthAgo;
    }
    return true;
  };

  const filtered = useMemo(() => leads.filter(l => {
    if (repFilter !== 'all' && l.salesman_id !== repFilter) return false;
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (!isInDateRange(l.created_at)) return false;
    if (search) {
      const q = search.toLowerCase();
      return (l.homeowner_name || '').toLowerCase().includes(q) ||
             (l.address || '').toLowerCase().includes(q) ||
             (l.salesman?.name || '').toLowerCase().includes(q);
    }
    return true;
  }), [leads, repFilter, statusFilter, dateFilter, search]);

  // Per-rep stats
  const repStats = useMemo(() => {
    const today = new Date().toDateString();
    return reps.map(r => {
      const myLeads = leads.filter(l => l.salesman_id === r.id);
      const todayLeads = myLeads.filter(l => new Date(l.created_at).toDateString() === today);
      return {
        ...r,
        total: myLeads.length,
        today: todayLeads.length,
        sold: myLeads.filter(l => l.status === 'sold').length,
        app: myLeads.filter(l => l.status === 'app').length,
      };
    }).sort((a, b) => b.sold - a.sold);
  }, [leads, reps]);

  const totalSold  = leads.filter(l => l.status === 'sold').length;
  const todayTotal = leads.filter(l => new Date(l.created_at).toDateString() === new Date().toDateString()).length;

  return (
    <div style={{ padding: 32, background: BG, minHeight: '100%' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: '#fff', fontSize: 28, fontWeight: 800, margin: 0 }}>📍 Team Activity</h1>
        <p style={{ color: MUTED, marginTop: 6, marginBottom: 0 }}>All field leads across your salesman team</p>
      </div>

      {/* Top Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Total Leads', value: leads.length, color: BRAND },
          { label: 'Doors Today', value: todayTotal, color: '#06B6D4' },
          { label: 'Total SOLDs', value: totalSold, color: SUCCESS },
          { label: 'Active Reps', value: reps.length, color: '#F59E0B' },
        ].map(s => (
          <div key={s.label} style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: '16px 18px' }}>
            <div style={{ color: s.color, fontSize: 28, fontWeight: 800 }}>{s.value}</div>
            <div style={{ color: MUTED, fontSize: 12, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Rep Cards */}
      {repStats.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, overflowX: 'auto', paddingBottom: 4 }}>
          {repStats.map(r => (
            <button key={r.id} onClick={() => setRepFilter(repFilter === r.id ? 'all' : r.id)} style={{
              background: repFilter === r.id ? BRAND + '33' : SURFACE,
              border: `1px solid ${repFilter === r.id ? BRAND : BORDER}`,
              borderRadius: 12, padding: '12px 16px', cursor: 'pointer', textAlign: 'left', flexShrink: 0, minWidth: 140,
            }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{r.name}</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: BRAND, fontWeight: 800, fontSize: 16 }}>{r.today}</div>
                  <div style={{ color: MUTED, fontSize: 10 }}>Today</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: SUCCESS, fontWeight: 800, fontSize: 16 }}>{r.sold}</div>
                  <div style={{ color: MUTED, fontSize: 10 }}>SOLDs</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>{r.total}</div>
                  <div style={{ color: MUTED, fontSize: 10 }}>Total</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search address or rep..."
          style={{ flex: 1, minWidth: 200, background: SURFACE, border: `1px solid ${BORDER}`, color: '#fff', padding: '9px 14px', borderRadius: 10, fontSize: 14 }} />

        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ background: SURFACE, border: `1px solid ${BORDER}`, color: '#fff', padding: '9px 14px', borderRadius: 10, fontSize: 13 }}>
          <option value="all">All Statuses</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>

        <select value={dateFilter} onChange={e => setDateFilter(e.target.value)}
          style={{ background: SURFACE, border: `1px solid ${BORDER}`, color: '#fff', padding: '9px 14px', borderRadius: 10, fontSize: 13 }}>
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="week">Last 7 Days</option>
          <option value="month">Last 30 Days</option>
        </select>
      </div>

      {/* Leads Table */}
      <div style={{ background: SURFACE, borderRadius: 16, border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
              {['Homeowner', 'Address', 'Status', 'Rep', 'Date'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#4A6580', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: MUTED }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: MUTED }}>No leads found</td></tr>
            ) : filtered.slice(0, 200).map(l => {
              const meta = STATUS_META[l.status] || { label: l.status, color: MUTED };
              return (
                <tr key={l.id} style={{ borderBottom: `1px solid ${BORDER}` }}>
                  <td style={{ padding: '12px 16px', color: '#fff', fontWeight: 600, fontSize: 13 }}>
                    {l.homeowner_name || '—'}
                    {l.claim_number && <div style={{ color: SUCCESS, fontSize: 11, marginTop: 2 }}>✓ Claim: {l.claim_number}</div>}
                  </td>
                  <td style={{ padding: '12px 16px', color: MUTED, fontSize: 13 }}>{l.address || '—'}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: meta.color + '22', color: meta.color, border: `1px solid ${meta.color}44`, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                      {meta.label}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', color: BRAND, fontSize: 13, fontWeight: 600 }}>{l.salesman?.name || '—'}</td>
                  <td style={{ padding: '12px 16px', color: MUTED, fontSize: 12 }}>
                    {new Date(l.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length > 200 && (
          <div style={{ padding: '12px 16px', color: MUTED, fontSize: 12, borderTop: `1px solid ${BORDER}` }}>
            Showing 200 of {filtered.length} leads. Use filters to narrow down.
          </div>
        )}
      </div>
    </div>
  );
}
